// src/store/useStore.js
import { create } from 'zustand';

export const useStore = create((set, get) => ({
  // ── Auth ──────────────────────────────────────────────────────────────────
  token: localStorage.getItem('obs_token') || null,
  user:  null,
  setAuth: (token, user) => {
    localStorage.setItem('obs_token', token);
    set({ token, user });
  },
  logout: () => {
    localStorage.removeItem('obs_token');
    set({ token: null, user: null });
  },

  // ── UI state ──────────────────────────────────────────────────────────────
  activeView: 'overview',
  sidebarOpen: true,
  setActiveView: (view) => set({ activeView: view }),
  toggleSidebar: () => set(s => ({ sidebarOpen: !s.sidebarOpen })),

  // ── Logs ──────────────────────────────────────────────────────────────────
  logs: [],
  logFilter: { source: 'ALL', level: 'ALL', search: '' },
  logsTotal: 0,
  addLogs: (newLogs) => set(s => {
    const seen = new Set(s.logs.map(l => l._id).filter(Boolean));
    const fresh = newLogs.filter(l => !l._id || !seen.has(l._id));
    return { logs: [...fresh, ...s.logs].slice(0, 500) };
  }),
  setLogFilter: (filter) => set(s => ({
    logFilter: { ...s.logFilter, ...filter },
  })),

  // ── Alerts ────────────────────────────────────────────────────────────────
  alerts: [],
  setAlerts: (alerts) => set({ alerts }),
  updateAlert: (updated) => set(s => ({
    alerts: s.alerts.map(a => a._id === updated._id ? updated : a),
  })),
  addAlert:    (alert)   => set(s => ({ alerts: [alert, ...s.alerts] })),
  removeAlert: (id)      => set(s => ({ alerts: s.alerts.filter(a => a._id !== id) })),

  // ── Sources ───────────────────────────────────────────────────────────────
  sources: [],
  setSources: (sources) => set({ sources }),

  // ── Analytics ─────────────────────────────────────────────────────────────
  analytics: null,
  setAnalytics: (analytics) => set({ analytics }),

  // ── Agent ─────────────────────────────────────────────────────────────────
  agentStatus: 'idle',    // idle | thinking | tool-call | complete
  agentTool:   null,
  agentFindings: null,
  chatSessionId: `session-${Date.now()}`,
  setAgentStatus: (status, tool = null) => set({ agentStatus: status, agentTool: tool }),
  setAgentFindings: (findings) => set({ agentFindings: findings, agentStatus: 'complete' }),

  // ── Dashboard ─────────────────────────────────────────────────────────────
  timeRange: '1h',          // 5m | 15m | 30m | 1h | 6h | 24h
  autoRefresh: 30,          // seconds; 0 = off
  variables: {
    source:      'ALL',     // ALL | GCP | AZURE | MONGODB | DATABASE
    severity:    'ALL',     // ALL | CRITICAL | WARNING | INFO
    environment: 'production',
  },
  annotations: [],
  showAnnotations: true,
  dashboardSaved: true,

  activeTemplate: 'infrastructure',
  setActiveTemplate: (activeTemplate) => set({ activeTemplate, dashboardSaved: false }),

  setTimeRange:       (timeRange)   => set({ timeRange, dashboardSaved: false }),
  setAutoRefresh:     (autoRefresh) => set({ autoRefresh }),
  setVariable:        (key, value)  => set(s => ({
    variables: { ...s.variables, [key]: value },
    dashboardSaved: false,
  })),
  setAnnotations:     (annotations) => set({ annotations }),
  addAnnotation:      (a)           => set(s => ({ annotations: [a, ...s.annotations] })),
  removeAnnotation:   (id)          => set(s => ({
    annotations: s.annotations.filter(a => a._id !== id),
  })),
  toggleAnnotations:  ()            => set(s => ({ showAnnotations: !s.showAnnotations })),
  markDashboardSaved: ()            => set({ dashboardSaved: true }),
}));
